/**
 * Dashboard Component
 * The authenticated user's home screen after login. Displays a personalized
 * overview of the user's ecosystem activity, including their profile summary,
 * organizations they manage, pending ownership claims, and network affiliations.
 *
 * Key behaviors:
 * - Fetches profile, organizations, claims, and affiliations in parallel on mount
 * - Displays a profile completion card when the profile is incomplete
 * - Gracefully handles broken images with initial-letter fallback avatars
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Spinner,
    Badge,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

import useCapability from '@/shared/hooks/useCapability';
import { useUserApi } from '@/shared/services/userApi';
import { useEcosystemApi } from '@/shared/services/ecosystemApi';
import useRedirectToApex from '@/shared/hooks/useRedirectToApex';

import ProfileCompletionCard from './components/ProfileCompletionCard';
import PlatformContextCard from './components/PlatformContextCard';
import './Dashboard.css';

// Maps affiliation type strings to Bootstrap badge color variants.
// Used in the "My Network" section to visually distinguish affiliation types.
const TYPE_BADGE_COLORS = {
    professional: 'primary',
    alumni: 'secondary',
    volunteer: 'success',
    collaborator: 'info',
    interested: 'warning',
};

export default function Dashboard() {
    // ---- State: User data fetched from the API ----
    const [profile, setProfile] = useState(null);
    const [myOrganizations, setMyOrganizations] = useState([]);
    const [myClaims, setMyClaims] = useState([]);
    const [myAffiliations, setMyAffiliations] = useState([]);

    // ---- State: UI concerns ----
    const [loading, setLoading] = useState(true);
    // Tracks broken images so we can fall back to initial-letter avatars.
    // profileImageError is a boolean; orgImageErrors is keyed by org._id.
    const [profileImageError, setProfileImageError] = useState(false);
    const [orgImageErrors, setOrgImageErrors] = useState({});

    // Per-tenant capability flags drive the Quick Actions tile gating.
    // Per the Wave Q brief the Ecosystem tile is gated on networkGraph
    // (the tile leads with the visual / graph experience). The /directory
    // ROUTE itself stays available (gated separately at the graph-component
    // level) so a tenant with networkGraph off still has the card view
    // reachable from the NavBar; the Dashboard tile is the heaviest
    // promotion of the graph and is the right place to hide it.
    const capNetworkGraph = useCapability('networkGraph');
    const capOpportunities = useCapability('opportunities');
    const capMentoring = useCapability('mentoring');
    const capDiscoverPartners = useCapability('discoverPartners');
    const capAiAssistant = useCapability('aiAssistant');
    const userApi = useUserApi();
    const ecosystemApi = useEcosystemApi();
    const [memberships, setMemberships] = useState([]);
    const [membershipsLoading, setMembershipsLoading] = useState(true);
    const [membershipsError, setMembershipsError] = useState(null);
    // Apex-only page. useRedirectToApex sends a tenant-nested URL back to the
    // bare apex, and the early return below means a render that reaches the copy
    // has always resolved to no tenant. So the copy names the platform: there is
    // no "this ecosystem" here to name, and templating one in would be copy that
    // cannot render.
    const isRedirectingToApex = useRedirectToApex('/dashboard');

    // Primary data-fetching effect: runs once on mount.
    // Fetches four user-specific datasets in parallel using Promise.all.
    // Individual .catch() wrappers on organizations, claims, and affiliations
    // ensure that a failure in one does not block the others or the profile fetch.
    useEffect(() => {
        if (isRedirectingToApex) return;
        const fetchDashboardData = async () => {
            try {
                const [profileData, orgsData, claimsData, affiliationsData] =
                    await Promise.all([
                        userApi.fetchProfile(),
                        userApi
                            .fetchMyOrganizations()
                            .catch(() => ({ organizations: [] })),
                        userApi.fetchMyClaims().catch(() => ({ claims: [] })),
                        userApi
                            .fetchMyAffiliations()
                            .catch(() => ({ affiliations: [] })),
                    ]);
                setProfile(profileData);
                setMyOrganizations(orgsData.organizations || []);
                setMyClaims(claimsData.claims || []);
                setMyAffiliations(affiliationsData.affiliations || []);
            } catch (err) {
                console.error('Failed to fetch dashboard data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [userApi, isRedirectingToApex]);

    // Memberships are fetched separately from the profile bundle above: they
    // drive the "where am I" card rather than the profile tiles, and a failure
    // here must not take the rest of the dashboard down with it.
    useEffect(() => {
        if (isRedirectingToApex) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await ecosystemApi.fetchMyMemberships();
                if (!cancelled) setMemberships(data.memberships || []);
            } catch (err) {
                console.error('Failed to fetch memberships:', err);
                if (!cancelled) {
                    setMembershipsError('Could not load your ecosystems.');
                }
            } finally {
                if (!cancelled) setMembershipsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ecosystemApi, isRedirectingToApex]);

    if (isRedirectingToApex) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Dashboard | RILE Connect</title>
            </Helmet>
            <Container className="py-5">
                {/* Greeting: names the platform for a new/incomplete profile
                    (< 50% complete), and the user for a returning one. */}
                <h1 className="text-primary mb-4 text-center text-lg-start">
                    {profile?.profileCompletion?.percentage < 50
                        ? 'Welcome to RILE Connect!'
                        : `Welcome back, ${profile?.name?.first || 'User'}!`}
                </h1>

                {/* States where the user actually is, and gives them the way
                    into an ecosystem. Signing in at /{slug}/login lands here at
                    the apex, so without this the page names no context at all
                    (#369). */}
                <PlatformContextCard
                    memberships={memberships}
                    loading={membershipsLoading}
                    error={membershipsError}
                />

                {/* Profile Row: displays the user's avatar, name, and email.
                    Uses an img tag with an onError fallback -- if the profile picture
                    URL fails to load, profileImageError is set to true and a
                    colored circle with the user's first initial is shown instead. */}
                <Row className="g-4 mb-4">
                    <Col xs={12}>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <div className="d-flex flex-column flex-md-row align-items-center gap-4">
                                    {profile?.profilePicture &&
                                    !profileImageError ? (
                                        <img
                                            src={profile.profilePicture}
                                            alt="Profile"
                                            className="rounded-circle flex-shrink-0 profile-thumbnail-lg"
                                            onError={() =>
                                                setProfileImageError(true)
                                            }
                                        />
                                    ) : (
                                        <div className="profile-thumbnail-lg rounded-circle bg-primary d-flex align-items-center justify-content-center flex-shrink-0 profile-avatar-fallback">
                                            {profile?.name?.first?.[0] || '?'}
                                        </div>
                                    )}
                                    <div className="text-center text-md-start flex-grow-1">
                                        <h5 className="text-primary mb-1">
                                            {profile?.name?.first}{' '}
                                            {profile?.name?.last}
                                        </h5>
                                        <p className="text-muted small mb-0">
                                            {profile?.email}
                                        </p>
                                    </div>
                                    <Button
                                        as={Link}
                                        to="/profile"
                                        variant="outline-primary"
                                        size="sm"
                                        className="rounded-pill"
                                    >
                                        My Profile
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Profile Completion: only shown when the profile is not yet 100% complete.
                    The ProfileCompletionCard displays a progress bar and lists missing fields. */}
                {profile?.profileCompletion &&
                    profile.profileCompletion.percentage < 100 && (
                        <Row className="g-4 mb-4">
                            <Col xs={12}>
                                <ProfileCompletionCard
                                    profileCompletion={
                                        profile.profileCompletion
                                    }
                                />
                            </Col>
                        </Row>
                    )}

                {/* Quick Actions: up to 5 tiles, each gated on its
                    respective per-tenant capability flag. A disabled
                    capability hides the corresponding tile entirely
                    (rather than show a non-functional CTA). The four-up
                    Bootstrap grid wraps naturally as tiles are added or
                    disappear. */}
                <Row className="g-4">
                    {capNetworkGraph && (
                        <Col sm={6} lg={3}>
                            <Card className="h-100 border-0 shadow-sm dashboard-card">
                                <Card.Body className="p-4 d-flex flex-column text-center">
                                    <div className="card-icon mb-3">🏢</div>
                                    <h5 className="text-primary">Ecosystem</h5>
                                    <p className="text-muted small flex-grow-1">
                                        Browse and discover STEM organizations
                                        across your ecosystems.
                                    </p>
                                    <div className="d-grid mt-auto">
                                        <Button
                                            as={Link}
                                            to="/directory?view=cards"
                                            variant="outline-primary"
                                            size="sm"
                                            className="rounded-pill"
                                        >
                                            Browse
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                    {capAiAssistant && (
                        <Col sm={6} lg={3}>
                            <Card className="h-100 border-0 shadow-sm dashboard-card">
                                <Card.Body className="p-4 d-flex flex-column text-center">
                                    <div className="d-flex justify-content-center align-items-center gap-2 mb-3 dashboard-persona-row">
                                        <img
                                            src="/images/personas/maya.png"
                                            alt="Maya"
                                            className="rounded-circle dashboard-persona-img"
                                        />
                                        <img
                                            src="/images/personas/aspen.png"
                                            alt="Aspen"
                                            className="rounded-circle dashboard-persona-img"
                                        />
                                        <img
                                            src="/images/personas/elena.png"
                                            alt="Elena"
                                            className="rounded-circle dashboard-persona-img"
                                        />
                                    </div>
                                    <h5 className="text-primary">
                                        AI Assistant
                                    </h5>
                                    <p className="text-muted small flex-grow-1">
                                        Chat with Aspen, Maya, or Elena to
                                        explore your ecosystems and find
                                        connections.
                                    </p>
                                    <div className="d-grid mt-auto">
                                        <Button
                                            as={Link}
                                            to="/assistant"
                                            variant="outline-primary"
                                            size="sm"
                                            className="rounded-pill"
                                        >
                                            Start Chat
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                    {capOpportunities && (
                        <Col sm={6} lg={3}>
                            <Card className="h-100 border-0 shadow-sm dashboard-card">
                                <Card.Body className="p-4 d-flex flex-column text-center">
                                    <div className="card-icon mb-3">🔍</div>
                                    <h5 className="text-primary">
                                        Opportunities
                                    </h5>
                                    <p className="text-muted small flex-grow-1">
                                        Find internships, workshops, grants, and
                                        events from organizations across your
                                        ecosystems.
                                    </p>
                                    <div className="d-grid mt-auto">
                                        <Button
                                            as={Link}
                                            to="/opportunities"
                                            variant="outline-primary"
                                            size="sm"
                                            className="rounded-pill"
                                        >
                                            Browse
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                    {capMentoring && (
                        <Col sm={6} lg={3}>
                            <Card className="h-100 border-0 shadow-sm dashboard-card">
                                <Card.Body className="p-4 d-flex flex-column text-center">
                                    <div className="card-icon mb-3">🧑‍🏫</div>
                                    <h5 className="text-primary">Mentors</h5>
                                    <p className="text-muted small flex-grow-1">
                                        Find mentors by expertise and
                                        availability across your ecosystems.
                                    </p>
                                    <div className="d-grid mt-auto">
                                        <Button
                                            as={Link}
                                            to="/connections/mentors"
                                            variant="outline-primary"
                                            size="sm"
                                            className="rounded-pill"
                                        >
                                            Browse
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                    {capDiscoverPartners && (
                        <Col sm={6} lg={3}>
                            <Card className="h-100 border-0 shadow-sm dashboard-card">
                                <Card.Body className="p-4 d-flex flex-column text-center">
                                    <div className="card-icon mb-3">🤝</div>
                                    <h5 className="text-primary">Partners</h5>
                                    <p className="text-muted small flex-grow-1">
                                        Discover partner organizations to
                                        collaborate with across your ecosystems.
                                    </p>
                                    <div className="d-grid mt-auto">
                                        <Button
                                            as={Link}
                                            to="/connections/partners"
                                            variant="outline-primary"
                                            size="sm"
                                            className="rounded-pill"
                                        >
                                            Browse
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                </Row>

                {/* My Organizations Section: lists organizations the user owns or manages,
                    followed by any pending ownership claims. When neither exists, an
                    empty state with a registration CTA is shown.
                    Badge color logic: owner = primary, admin = info, other = secondary.
                    Organizations with status 'pending' show an additional warning badge. */}
                <Row className="g-4 mt-4">
                    <Col xs={12}>
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                                <strong>My Organizations</strong>
                                <Link
                                    to="/organizations/onboard"
                                    className="btn btn-sm btn-outline-primary rounded-pill dashboard-header-btn"
                                >
                                    + Add Organization
                                </Link>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {myOrganizations.length === 0 &&
                                myClaims.length === 0 ? (
                                    <div className="p-4 text-center text-muted">
                                        <p className="mb-2">
                                            You are not managing any
                                            organizations yet.
                                        </p>
                                        <Link
                                            to="/organizations/onboard"
                                            className="btn btn-primary rounded-pill"
                                        >
                                            Register an Organization
                                        </Link>
                                    </div>
                                ) : (
                                    <div className="list-group list-group-flush">
                                        {myOrganizations.map((org) => (
                                            <Link
                                                key={org._id}
                                                to={`/organizations/${org._id}`}
                                                className="list-group-item list-group-item-action"
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="d-flex align-items-center gap-3">
                                                        {org.imageUrl &&
                                                        !orgImageErrors[
                                                            org._id
                                                        ] ? (
                                                            <img
                                                                src={
                                                                    org.imageUrl
                                                                }
                                                                alt={org.name}
                                                                className="rounded org-thumbnail-sm"
                                                                onError={() =>
                                                                    setOrgImageErrors(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            [org._id]: true,
                                                                        })
                                                                    )
                                                                }
                                                            />
                                                        ) : (
                                                            <div className="rounded bg-light d-flex align-items-center justify-content-center org-placeholder-sm">
                                                                <span className="text-muted">
                                                                    {org.name.charAt(
                                                                        0
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <strong>
                                                                {org.name}
                                                            </strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                {
                                                                    org.primarySector
                                                                }
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <Badge
                                                            bg={
                                                                org.userPermissionRole ===
                                                                'owner'
                                                                    ? 'primary'
                                                                    : org.userPermissionRole ===
                                                                        'admin'
                                                                      ? 'info'
                                                                      : 'secondary'
                                                            }
                                                            className="role-badge-fixed"
                                                        >
                                                            {
                                                                org.userPermissionRole
                                                            }
                                                        </Badge>
                                                        {org.status ===
                                                            'pending' && (
                                                            <Badge bg="warning">
                                                                Pending
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                        {/* Pending Claims */}
                                        {myClaims.map((claim, index) => (
                                            <div
                                                key={
                                                    claim.claimId ||
                                                    claim.organization?._id ||
                                                    `claim-${index}`
                                                }
                                                className="list-group-item"
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="d-flex align-items-center gap-3">
                                                        {claim.organization
                                                            ?.imageUrl &&
                                                        !orgImageErrors[
                                                            claim.organization
                                                                ?._id
                                                        ] ? (
                                                            <img
                                                                src={
                                                                    claim
                                                                        .organization
                                                                        .imageUrl
                                                                }
                                                                alt={
                                                                    claim
                                                                        .organization
                                                                        .name
                                                                }
                                                                className="rounded org-thumbnail-sm"
                                                                onError={() =>
                                                                    setOrgImageErrors(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            [claim
                                                                                .organization
                                                                                ._id]:
                                                                                true,
                                                                        })
                                                                    )
                                                                }
                                                            />
                                                        ) : (
                                                            <div className="rounded bg-light d-flex align-items-center justify-content-center org-placeholder-sm">
                                                                <span className="text-muted">
                                                                    {claim.organization?.name?.charAt(
                                                                        0
                                                                    ) || '?'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <strong>
                                                                {
                                                                    claim
                                                                        .organization
                                                                        .name
                                                                }
                                                            </strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                Ownership claim
                                                                pending
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <Badge bg="warning">
                                                        Claim Pending
                                                    </Badge>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* My Network (Affiliations) Section: shows organizations the user has
                    affiliated with (e.g., as a professional, volunteer, alumni).
                    This is distinct from "My Organizations" above -- affiliations are
                    lightweight connections, while organizations represent management roles.
                    Each affiliation's badge color is determined by TYPE_BADGE_COLORS.
                    The header links to the user's profile page network graph view. */}
                <Row className="g-4 mt-4">
                    <Col xs={12}>
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                                <Link
                                    to="/profile#network-visualization"
                                    className="text-decoration-none d-flex align-items-center gap-2"
                                >
                                    <strong className="text-dark">
                                        My Network
                                    </strong>
                                    <small className="text-primary">
                                        View Graph →
                                    </small>
                                </Link>
                                <Link
                                    to="/profile"
                                    className="btn btn-sm btn-outline-primary rounded-pill dashboard-header-btn"
                                >
                                    + Add Affiliation
                                </Link>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {myAffiliations.length === 0 ? (
                                    <div className="p-4 text-center text-muted">
                                        <p className="mb-2">
                                            No affiliations yet. Connect with
                                            organizations across your
                                            ecosystems.
                                        </p>
                                        <Link
                                            to="/profile"
                                            className="btn btn-primary rounded-pill"
                                        >
                                            Add Your First Affiliation
                                        </Link>
                                    </div>
                                ) : (
                                    <div className="list-group list-group-flush">
                                        {myAffiliations.map((aff) => (
                                            <Link
                                                key={aff.organization?._id}
                                                to={`/organizations/${aff.organization?._id}`}
                                                className="list-group-item list-group-item-action"
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="d-flex align-items-center gap-3">
                                                        {aff.organization
                                                            ?.imageUrl &&
                                                        !orgImageErrors[
                                                            aff.organization
                                                                ?._id
                                                        ] ? (
                                                            <img
                                                                src={
                                                                    aff
                                                                        .organization
                                                                        .imageUrl
                                                                }
                                                                alt={
                                                                    aff
                                                                        .organization
                                                                        .name
                                                                }
                                                                className="rounded org-thumbnail-sm"
                                                                onError={() =>
                                                                    setOrgImageErrors(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            [aff
                                                                                .organization
                                                                                ._id]:
                                                                                true,
                                                                        })
                                                                    )
                                                                }
                                                            />
                                                        ) : (
                                                            <div className="rounded bg-light d-flex align-items-center justify-content-center org-placeholder-sm">
                                                                <span className="text-muted">
                                                                    {aff.organization?.name?.charAt(
                                                                        0
                                                                    ) || '?'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <strong>
                                                                {
                                                                    aff
                                                                        .organization
                                                                        ?.name
                                                                }
                                                            </strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                {
                                                                    aff
                                                                        .organization
                                                                        ?.primarySector
                                                                }
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <Badge
                                                        bg={
                                                            TYPE_BADGE_COLORS[
                                                                aff.type
                                                            ] || 'secondary'
                                                        }
                                                        className="role-badge-fixed"
                                                    >
                                                        {aff.type}
                                                    </Badge>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
